import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import SignatureCanvas from 'react-signature-canvas';

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (signatureDataUrl: string, file: File) => void;
  title?: string;
  subtitle?: string;
}

const SignatureModal: React.FC<SignatureModalProps> = ({
  open,
  onClose,
  onSave,
  title = 'Signature',
  subtitle = 'Veuillez signer dans le champ ci-dessous'
}) => {
  const signatureRef = useRef<SignatureCanvas>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const handleClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setIsEmpty(true);
    }
  };

  const handleBegin = () => {
    setIsEmpty(false);
  };

  const handleEnd = () => {
    if (signatureRef.current) {
      const isEmpty = signatureRef.current.isEmpty();
      setIsEmpty(isEmpty);
    }
  };

  const handleSave = () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      return;
    }

    // Get signature as data URL
    const dataURL = signatureRef.current.toDataURL('image/png');
    
    // Convert data URL to File
    const byteString = atob(dataURL.split(',')[1]);
    const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString });
    const file = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' });
    
    onSave(dataURL, file);
    handleClear();
    onClose();
  };

  const handleClose = () => {
    handleClear();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              ✍️ {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Utilisez votre souris ou votre doigt pour signer dans le champ ci-dessous.
          </Alert>
        </Box>

        <Box
          sx={{
            border: '2px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <SignatureCanvas
            ref={signatureRef}
            canvasProps={{
              width: 600,
              height: 300,
              className: 'signature-canvas'
            }}
            onBegin={handleBegin}
            onEnd={handleEnd}
            backgroundColor="#ffffff"
            penColor="#000000"
            minWidth={2}
            maxWidth={3}
            throttle={16}
          />
        </Box>

        {isEmpty && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            La signature est vide
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleClear}
          startIcon={<ClearIcon />}
          disabled={isEmpty}
          variant="outlined"
        >
          Effacer
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} variant="outlined">
          Annuler
        </Button>
        <Button
          onClick={handleSave}
          startIcon={<CheckIcon />}
          disabled={isEmpty}
          variant="contained"
          sx={{ backgroundColor: '#ff6b35', '&:hover': { backgroundColor: '#e55a2b' } }}
        >
          Enregistrer la signature
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignatureModal;

